import { createContext, FC, useContext, useMemo } from 'react';

import { t } from '@core/i18n';
import { RequirementStatus } from '@shared/apis/requirementsApi.types';
import { useConfigAll } from '@shared/contexts/ConfigContext';
import { ConfigCoreConstants } from '@shared/models/config';
import { IUser } from '@shared/models/IUser';
import { SocietyAcquisitionStatus } from '@shared/models/society/societyAcquisitions';
import { ViewingOutcome } from '@shared/models/viewing/viewings';
import { getMonthQuarterLabelsMap } from '@shared/types/common/month';
import {
  availableLettingTypeOptionsToSelectOptions,
  filterOptions,
  idArrayToNumberOptions,
  objectOptionsToOptions,
  objectToOptions,
  OptionsItem,
  optionsToFlatGroupOptions,
  sourceOptionsSelectOptions,
  stringObjectToNumberOptions,
  stringObjectToOptions,
} from '@shared/utils/options';

export const getCoreOptionsValue = (constants: ConfigCoreConstants, user: IUser) => {
  // All (used by others below)
  const allBuildingTypeOptions = stringObjectToOptions(constants.buildingTypes);
  const allClassOfUseOptions = stringObjectToOptions(constants.classOfUse);
  const allSectorOptions = stringObjectToOptions(constants.sectors);
  const allSourceOptions = sourceOptionsSelectOptions(constants.localisation.sourceOptions);
  const buildingTypeGroupOptions = objectOptionsToOptions(constants.buildingTypeGroups);

  // Options (used by others below)
  const acquisitionFittedSpaceOptions = stringObjectToOptions(constants.acquisitionFittedSpaceOptions);
  const classOfUseVisibleOptions = filterOptions(
    allClassOfUseOptions,
    user.organisation?.settings?.use_classes_visible,
    true
  );
  const classOfUseOptions =
    constants.preferredUseClasses?.length > 0
      ? filterOptions(classOfUseVisibleOptions, constants.preferredUseClasses, true)
      : classOfUseVisibleOptions;
  const investmentProfileOptions = stringObjectToOptions(constants.investmentProfileOptions);
  const investmentStrategyOptions = stringObjectToOptions(constants.investmentStrategyOptions);
  const investmentStructureOptions = stringObjectToOptions(constants.investmentStructureOptions);
  const leaseTypeOptions = stringObjectToOptions(constants.leaseTypes);
  const preferredBuildingTypeOptions = allBuildingTypeOptions.filter((option) =>
    constants.preferredBuildingTypes.includes(option.id)
  );
  const preferredSectorOptions = filterOptions(allSectorOptions, constants.preferredSectors, true);
  const preferredSourceOptions = allSourceOptions.filter((option) => constants.preferredSources.includes(option.id));
  const requirementReasonOptions = stringObjectToOptions(constants.requirementReasonOptions);
  const requirementStatusOptions = idArrayToNumberOptions<RequirementStatus>(constants.requirementStatusOptions);
  const societyAcquisitionStatusOptions = idArrayToNumberOptions<SocietyAcquisitionStatus>(
    constants.societyAcquisitionStatusOptions
  );
  const sectorOptions = filterOptions(allSectorOptions, user.organisation?.settings.sectors_visible, true);

  // Static / processed options
  const acquisitionAdvertTypeOptions: OptionsItem<number>[] = constants.acquisitionAdvertTypeOptions.map(
    (label, idx) => ({ id: idx, label })
  );
  const booleanOptions: OptionsItem<NumberBoolean>[] = [
    { id: 1, label: t('yes') },
    { id: 0, label: t('no') },
  ];
  const booleanStringOptions: OptionsItem<StringBoolean>[] = [
    { label: t('yes'), id: '1' },
    { label: t('no'), id: '0' },
  ];
  const booleanSpecOptions: OptionsItem<string>[] = [
    { label: t('yes'), id: t('yes') },
    { label: t('no'), id: t('no') },
  ];
  const confidentialOptions: OptionsItem<NumberBoolean>[] = [
    { label: t('not_confidential'), id: 0 },
    { label: t('confidential'), id: 1 },
  ];
  const contactTitleOptions: OptionsItem<number>[] = [
    { id: 1, label: t('mr') },
    { id: 2, label: t('mrs') },
    { id: 3, label: t('ms') },
    { id: 4, label: t('miss') },
    { id: 5, label: t('dr') },
    { id: 6, label: t('professor') },
    { id: 7, label: t('reverend') },
    { id: 8, label: t('pastor') },
    { id: 9, label: t('rabbi') },
    { id: 10, label: t('sister') },
  ];
  const currencyOptions: OptionsItem<string>[] = constants.localisation.currencyOptionsDropdown.map((option) => ({
    id: option.id,
    label: option.name,
  }));

  const matchDisposalsPublishedOptions: OptionsItem<string>[] = [
    { id: 'yes', label: t('only_show_acquisitions_ive_published_to_the_society') },
    { id: 'no', label: t('show_all_my_acquisitions_including_those_not_published_to_the_society') },
  ];
  const matchDisposalsRadioOptions: OptionsItem<string>[] = [
    { id: 'my_disposals', label: t('my') },
    { id: 'team_disposals', label: t('team') },
    { id: 'all_disposals', label: t('all') },
  ];
  const matchDisposalsTenureOptions: OptionsItem<string>[] = [
    { id: 'to_let', label: t('to_let') },
    { id: 'for_sale', label: t('for_sale') },
    { id: 'to_let,for_sale', label: t('to_let_and_for_sale') },
  ];

  const monthQuarterOptions: OptionsItem<number>[] = Object.entries(getMonthQuarterLabelsMap()).map(([id, label]) => ({
    id: parseInt(id, 10),
    label,
  }));
  const requirementListFilterOptions = constants.requirementListOptions
    .filter((option) => option.can_filter)
    .map((option) => ({
      id: option.id,
      label: option.name,
    }));
  const societyDisposalStatuses: OptionsItem<number>[] = [
    { label: t('coming_soon'), id: 1 },
    { label: t('available'), id: 2 },
    { label: t('under_offer'), id: 3 },
    { label: t('withdrawn'), id: 5 },
  ];
  const societyDisposalAdvertStatuses: OptionsItem<number>[] = [
    { label: t('coming_soon'), id: 1 },
    { label: t('available'), id: 2 },
    { label: t('under_offer'), id: 3 },
    { label: t('let'), id: 4 },
    { label: t('sold'), id: 7 },
    { label: t('withdrawn'), id: 5 },
  ];

  const viewingFeedbackRadioOptions: OptionsItem<ViewingOutcome>[] = [
    { id: ViewingOutcome.Positive, label: t('positive') },
    { id: ViewingOutcome.Neutral, label: t('neutral') },
    { id: ViewingOutcome.Negative, label: t('negative') },
  ];
  const viewingFeedbackMultiSelectOptions: OptionsItem<ViewingOutcome>[] = [
    { id: ViewingOutcome.Pending, label: t('pending_feedback') },
    { id: ViewingOutcome.None, label: t('no_feedback_given') },
    { id: ViewingOutcome.Positive, label: t('positive_feedback') },
    { id: ViewingOutcome.Neutral, label: t('neutral_feedback') },
    { id: ViewingOutcome.Negative, label: t('negative_feedback') },
    { id: ViewingOutcome.Cancelled, label: t('cancelled_hyphen_no_longer_interested') },
  ];

  return {
    // ---- Options ----

    acquisitionAdvertTypeOptions,
    acquisitionFittedSpaceOptions,
    availableLettingTypeOptions: availableLettingTypeOptionsToSelectOptions(constants.availableLettingTypeOptions),
    availableSizeMetricOptions: stringObjectToOptions<Metric>(constants.availableSizeMetrics),
    booleanOptions,
    booleanStringOptions,
    booleanSpecOptions,
    breeamRatingOptions: stringObjectToOptions(constants.breeamRatingOptions),
    buildStatusOptions: objectToOptions(constants.buildStatusOptions),
    buildingTypeGroupOptions,
    buildingTypeOptions: allBuildingTypeOptions,
    classOfUseOptions,
    confidentialOptions,
    contactTitleOptions,
    currencyOptions,
    discountReasonOptions: stringObjectToOptions(constants.matchDiscountReasonOptions),
    disposalGradeOptions: objectToOptions(constants.disposalGradeOptions),
    declineInviteReasonOptions: stringObjectToOptions(constants.declineInviteReasonOptions),
    disposalFittedSpaceOptions: stringObjectToOptions(constants.disposalFittedSpaceOptions),
    epcBandOptions: objectToOptions(constants.epcBandOptions),
    fitoutConceptOptions: objectToOptions(constants.fitoutConceptOptions),
    fitoutConditionOptions: objectToOptions(constants.fitoutConditionOptions),
    floorConsiderationsOptions: stringObjectToOptions(constants.floorConsiderations),
    floorUnitNameOptions: stringObjectToOptions(constants.floorUnitNameOptions),
    interestScheduleShareRequirementOptions: stringObjectToOptions(constants.interestScheduleShareRequirementOptions),
    investmentProfileOptions,
    investmentStrategyOptions,
    investmentStructureOptions,
    leadQualityOptions: stringObjectToOptions(constants.leadQualityOptions),
    leaseTypeOptions,
    matchDisposalsPublishedOptions,
    matchDisposalsRadioOptions,
    matchDisposalsTenureOptions,
    monthQuarterOptions,
    ourInstructionsOptions: stringObjectToOptions(constants.ourInstructionsOptions),
    ownershipTypeOptions: stringObjectToNumberOptions(constants.availableSaleTypeOptions),
    preferredBuildingTypeOptions,
    preferredSectorOptions,
    preferredSourceOptions,
    rentMetricsOptions: stringObjectToOptions<Metric>(constants.rentMetrics),
    filterableRentMetricOptions: stringObjectToOptions<Metric>(constants.rentMetricsFilterable),
    requirementListFilterOptions,
    requirementReasonOptions,
    requirementStatusOptions,
    requirementTenureTypeOptions: stringObjectToOptions(constants.requirementTenureTypeOptions),
    saleTenancyStatusOptions: stringObjectToNumberOptions(constants.tenancyStatusOptions),
    sectorOptions: sectorOptions,
    societyAcquisitionStatusOptions,
    societyDisposalAdvertStatuses,
    societyDisposalStatuses,
    sourceOptions: allSourceOptions,
    tenancyStatusOptions: stringObjectToNumberOptions(constants.targetTenancyDetails),
    viewingFeedbackMultiSelectOptions,
    viewingFeedbackRadioOptions,

    // ---- Grouped options ----

    groupedPreferredSectorOptions: optionsToFlatGroupOptions(preferredSectorOptions),
    groupedPreferredSourceOptions: optionsToFlatGroupOptions(preferredSourceOptions),
  };
};

export type UseCoreOptionsResult = ReturnType<typeof getCoreOptionsValue>;

const OptionsContext = createContext<UseCoreOptionsResult>({} as UseCoreOptionsResult);

export const useCoreOptions = () => {
  return useContext(OptionsContext);
};

export const CoreOptionsProvider: FC = ({ children }) => {
  const { config, constants } = useConfigAll();

  const result = useMemo(() => getCoreOptionsValue(constants, config.user), [constants, config.user]);

  return <OptionsContext.Provider value={result}>{children}</OptionsContext.Provider>;
};

export const useEmptyOption = <TValue extends Id = Id>(
  options: OptionsItem<TValue>[],
  { label = '', required = false } = {}
) => {
  return useMemo<OptionsItem<TValue | ''>[]>(() => {
    if (required) {
      return options;
    }

    return [{ id: '', label: label || t('please_select') }, ...options];
  }, [label, options, required]);
};
